import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type {
	ComponentType,
	MouseEventHandler,
	ReactNode,
	SVGProps,
} from 'react';

import type { UnpluginIcon } from '@adflow/frontend/ui-assets';
import { ButtonIconAddWithText } from '@adflow/frontend/ui-buttons';
import { su, theme, typography } from '@adflow/frontend/ui-theme';

import type { AddMenuOption } from './AddMenu';
import { AddMenu } from './AddMenu';

const styles = {
	container: css({
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '20rem',
	}),
	icon: css({
		width: '100%',
		height: '100%',
		marginLeft: 'auto',
		marginRight: 'auto',
		marginBottom: '2rem',
		maxWidth: '6.5rem',
		maxHeight: '6.5rem',
		color: theme.colors.text.hint,
	}),
	message: css(typography.emptyLarge, {
		textAlign: 'center',
		maxWidth: '40rem',
	}),
	// font is heavier to make lighter text more visible
	missingPerm: css({
		color: theme.colors.text.permission,
		fontWeight: '400',
	}),
	addButton: css(typography.clickableLink, su.textMd, {
		color: theme.colors.text.tableHeader,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: '2.5rem',
		paddingRight: '2.5rem',
		paddingTop: '0.75rem',
		paddingBottom: '0.75rem',
		cursor: 'pointer',
	}),

	addButtonIcon: css({
		width: '1.25rem',
		height: '1.25rem',
		marginLeft: '0.5rem',
	}),
};

interface BaseEmptySectionProps {
	outerStyle?: CSSInterpolation;
	Icon: UnpluginIcon | ComponentType<SVGProps<SVGSVGElement>>;
	iconStyle?: CSSInterpolation;
	message?: string | ReactNode;
	messageStyle?: CSSInterpolation;
	missingPermission?: true;
}

interface AddProps {
	onAddClick?: MouseEventHandler<HTMLButtonElement>;
	addOptions?: AddMenuOption[];
	addText: string;
}

type EmptySectionProps =
	| (BaseEmptySectionProps & { onAddClick?: never; addOptions?: never })
	| (BaseEmptySectionProps & AddProps);

export function EmptySection(props: EmptySectionProps) {
	return (
		<div css={[styles.container, props.outerStyle]} data-testid="empty-section">
			<props.Icon
				css={[
					styles.icon,
					props.iconStyle,
					props.missingPermission && styles.missingPerm,
				]}
			/>
			{props.message ? (
				<div
					css={[
						styles.message,
						props.messageStyle,
						props.missingPermission && styles.missingPerm,
					]}
				>
					{props.message}
				</div>
			) : null}
			{props.onAddClick ? (
				<ButtonIconAddWithText
					side="left"
					text={props.addText}
					buttonStyle={styles.addButton}
					onClick={props.onAddClick}
				/>
			) : null}
			{props.addOptions ? (
				<AddMenu
					addOptions={props.addOptions}
					label={props.addText}
					addButtonStyles={styles.addButton}
					buttonIconStyles={styles.addButtonIcon}
					listTestid="empty-section-add-list"
				/>
			) : null}
		</div>
	);
}
